/**
 * AgentQueryNotification.js
 * 
 * Floating notification widget that surfaces pending agent questions to the user.
 * Agents (Ledger, Cortex, Sentinel, etc.) can post questions via the AgentUserQuery
 * model. This component polls for pending queries and lets users respond inline.
 *
 * API endpoints:
 *   GET  /agent-queries              → pending queries for authenticated user
 *   POST /agent-queries/:id/respond  → { answer }
 *   POST /agent-queries/:id/cancel   → dismiss a query
 *
 * Renders as a fixed FAB (bottom-right) with a badge count. Clicking opens a card
 * showing the current question, choice chips, and optional freeform input.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Paper,
  Typography,
  Button,
  TextField,
  IconButton,
  Badge,
  Fab,
  Chip,
  Box,
  Stack,
  CircularProgress,
  Fade,
  Divider
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import api from '../services/api';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const POLL_INTERVAL_MS = 15_000;

// Agent display names — friendlier than raw agentId strings
const AGENT_LABELS = {
  ledger: '💰 Ledger',
  cortex: '🧠 Cortex',
  sentinel: '🛡️ Sentinel',
  vision: '📷 Vision',
  vault: '🔐 Vault',
  nexus: '🔗 Nexus',
  watchtower: '👁️ Watchtower',
  prism: '🎨 Prism',
  forge: '🔨 Forge',
  atlas: '🗺️ Atlas',
  babel: '🌐 Babel',
  apollo: '☀️ Apollo',
};

const getAgentLabel = (agentId) => {
  if (!agentId) return '🤖 AI Agent';
  const key = agentId.toLowerCase().replace(/^agent[-_]?/, '');
  return AGENT_LABELS[key] || `🤖 ${agentId}`;
};

// ---------------------------------------------------------------------------
// Priority badge colours
// ---------------------------------------------------------------------------

const PRIORITY_COLOR = {
  critical: 'error',
  high: 'warning',
  medium: 'info',
  low: 'default',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const AgentQueryNotification = () => {
  const [queries, setQueries] = useState([]);
  const [open, setOpen] = useState(false);
  const [freeformAnswer, setFreeformAnswer] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const inputRef = useRef(null);

  // -----------------------------------------------------------------------
  // Fetch pending queries
  // -----------------------------------------------------------------------

  const fetchQueries = useCallback(async () => {
    // Skip polling when no auth token — avoids 401 spam
    const token = localStorage.getItem('token');
    if (!token) { setQueries([]); return; }

    try {
      // Explicitly include the Authorization header in addition to the global
      // interceptor in api.js.  This ensures the Bearer token is always
      // forwarded even if the interceptor's localStorage read races with a
      // concurrent token-rotation event, and makes the intent unambiguous.
      const { data } = await api.get('/agent-queries', {
        headers: {
          Authorization: `Bearer ${token}`,
          'x-auth-token': token,
        },
      });

      if (data.success && Array.isArray(data.queries) && data.queries.length > 0) {
        setQueries(data.queries);
      } else {
        setQueries([]);
        if (open) setOpen(false);
      }
    } catch (err) {
      // 401 / 403 are expected when a session expires — suppress noisy
      // console output for these; the global api.js interceptor handles
      // token cleanup and dispatches the auth:error event.
      if (err?.response?.status !== 401 && err?.response?.status !== 403) {
        console.error('[AgentQuery] poll failed:', err.message);
      }
    }
  }, [open]);

  useEffect(() => {
    fetchQueries();
    const interval = setInterval(fetchQueries, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchQueries]);

  // Keep currentIndex in bounds when the list shrinks
  useEffect(() => {
    if (currentIndex >= queries.length) {
      setCurrentIndex(Math.max(0, queries.length - 1));
    }
  }, [queries.length, currentIndex]);

  // Auto-focus freeform input when the card opens
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus();
    }
  }, [open]);

  // -----------------------------------------------------------------------
  // Respond to a query
  // -----------------------------------------------------------------------

  const handleRespond = async (queryId, answer) => {
    if (!answer || submitting) return;
    setSubmitting(true);
    try {
      await api.post(`/agent-queries/${queryId}/respond`, { answer });
      setFreeformAnswer('');
      // Optimistic removal
      setQueries((prev) => prev.filter((q) => q._id !== queryId));
    } catch (err) {
      console.error('[AgentQuery] respond failed:', err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // -----------------------------------------------------------------------
  // Dismiss / cancel a query
  // -----------------------------------------------------------------------

  const handleCancel = async (queryId) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.post(`/agent-queries/${queryId}/cancel`);
      // Optimistic removal
      setQueries((prev) => prev.filter((q) => q._id !== queryId));
    } catch (err) {
      console.error('[AgentQuery] cancel failed:', err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // -----------------------------------------------------------------------
  // Navigate between multiple queries
  // -----------------------------------------------------------------------

  const handleNext = () => {
    setFreeformAnswer('');
    setCurrentIndex((prev) => (prev + 1) % queries.length);
  };

  // -----------------------------------------------------------------------
  // Render nothing when there are no pending queries
  // -----------------------------------------------------------------------

  if (queries.length === 0) return null;

  const currentQuery = queries[currentIndex] || queries[0];
  const agentLabel = getAgentLabel(currentQuery.agentId);
  const priorityColor = PRIORITY_COLOR[currentQuery.priority] || 'primary';

  // -----------------------------------------------------------------------
  // JSX
  // -----------------------------------------------------------------------

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: { xs: 80, sm: 24 }, // above mobile bottom-nav on xs
        right: { xs: 12, sm: 24 },
        zIndex: 1300,
      }}
    >
      {/* ---- Collapsed: FAB with badge ---- */}
      {!open && (
        <Fade in>
          <Badge
            badgeContent={queries.length}
            color="error"
            overlap="circular"
            sx={{
              '& .MuiBadge-badge': {
                fontWeight: 700,
                fontSize: '0.7rem',
              },
            }}
          >
            <Fab
              color="primary"
              size="medium"
              onClick={() => setOpen(true)}
              aria-label={`${queries.length} pending agent question${queries.length > 1 ? 's' : ''}`}
              sx={{
                boxShadow: 6,
                animation: 'pulse 2s infinite',
                '@keyframes pulse': {
                  '0%': { boxShadow: '0 0 0 0 rgba(16,185,129,0.5)' },
                  '70%': { boxShadow: '0 0 0 10px rgba(16,185,129,0)' },
                  '100%': { boxShadow: '0 0 0 0 rgba(16,185,129,0)' },
                },
              }}
            >
              <SmartToyIcon />
            </Fab>
          </Badge>
        </Fade>
      )}

      {/* ---- Expanded: Question card ---- */}
      {open && (
        <Fade in>
          <Paper
            elevation={10}
            sx={{
              width: { xs: 'calc(100vw - 24px)', sm: 360 },
              maxHeight: '70vh',
              overflowY: 'auto',
              p: 2,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            {/* Header */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
              <Stack direction="row" spacing={1} alignItems="center">
                <SmartToyIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" fontWeight={700} noWrap>
                  {agentLabel}
                </Typography>
                {currentQuery.priority && currentQuery.priority !== 'medium' && (
                  <Chip
                    label={currentQuery.priority}
                    color={priorityColor}
                    size="small"
                    sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600 }}
                  />
                )}
              </Stack>
              <IconButton size="small" onClick={() => setOpen(false)} aria-label="Minimise">
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>

            <Divider sx={{ mb: 1.5 }} />

            {/* Question */}
            <Typography variant="body2" sx={{ mb: 1.5, whiteSpace: 'pre-wrap' }}>
              {currentQuery.question}
            </Typography>

            {/* Context / reason */}
            {currentQuery.context?.reason && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  mb: 1.5,
                  display: 'block',
                  fontStyle: 'italic',
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                  px: 1,
                  py: 0.5,
                }}
              >
                💡 {currentQuery.context.reason}
              </Typography>
            )}

            {/* Choice chips */}
            {Array.isArray(currentQuery.choices) && currentQuery.choices.length > 0 && (
              <Stack
                direction="row"
                flexWrap="wrap"
                sx={{ mb: 1.5, gap: 0.75 }}
              >
                {currentQuery.choices.map((choice, i) => (
                  <Chip
                    key={i}
                    label={choice}
                    clickable
                    color="primary"
                    variant="outlined"
                    disabled={submitting}
                    onClick={() => handleRespond(currentQuery._id, choice)}
                    sx={{ fontWeight: 500 }}
                  />
                ))}
              </Stack>
            )}

            {/* Freeform text input */}
            {currentQuery.allowFreeform !== false && (
              <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                <TextField
                  inputRef={inputRef}
                  size="small"
                  fullWidth
                  placeholder="Type your answer…"
                  value={freeformAnswer}
                  disabled={submitting}
                  onChange={(e) => setFreeformAnswer(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && freeformAnswer.trim()) {
                      e.preventDefault();
                      handleRespond(currentQuery._id, freeformAnswer.trim());
                    }
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': { borderRadius: 2 },
                  }}
                />
                <Button
                  variant="contained"
                  size="small"
                  disabled={!freeformAnswer.trim() || submitting}
                  onClick={() => handleRespond(currentQuery._id, freeformAnswer.trim())}
                  sx={{ minWidth: 40, px: 1 }}
                  aria-label="Send answer"
                >
                  {submitting ? <CircularProgress size={18} color="inherit" /> : <SendIcon fontSize="small" />}
                </Button>
              </Stack>
            )}

            {/* Footer: dismiss + counter + next */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" mt={1.5}>
              <Button
                size="small"
                color="inherit"
                disabled={submitting}
                onClick={() => handleCancel(currentQuery._id)}
                sx={{ textTransform: 'none', fontSize: '0.75rem' }}
              >
                Dismiss
              </Button>

              {queries.length > 1 && (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography variant="caption" color="text.secondary">
                    {currentIndex + 1}/{queries.length}
                  </Typography>
                  <IconButton size="small" onClick={handleNext} aria-label="Next question">
                    <SkipNextIcon fontSize="small" />
                  </IconButton>
                </Stack>
              )}
            </Stack>
          </Paper>
        </Fade>
      )}
    </Box>
  );
};

export default AgentQueryNotification;
